// src/ui/steps/step3-festival-werkdagen.jsx: Festival ZZP-flow stap 3
// Werkdagen-invoer (één veld). Eén werkdag = 7,6 uur (excl. pauze, conform
// SAV): we tonen de omrekening naar uren in de lopend-totaal-AsideCard
// zodat de zzp'er ziet hoeveel uur er onder de werkdagen zit. Prijzen worden
// hier nog NIET getoond: die volgen in stap 5. Festivaldag-toeslag,
// nachtwerk, lunch/diner en reisuren komen als generieke onderhandelpunten
// in stap 5 terug.
import React from "react";
import {
  FW, FwButton,
  StepHeader, NiveauBadge, AsideCard,
} from "../primitives";

const UREN_PER_WERKDAG = 7.6;

export const StepFestivalWerkdagen = ({ state, setState, next, back }) => {
  const huidigeRol = state.rollen?.[0];

  const [voorbereiding, setVoorbereiding] = React.useState(huidigeRol?.werkdagen?.voorbereiding ?? "");
  const [festivaldagen, setFestivaldagen] = React.useState(huidigeRol?.werkdagen?.festivaldagen ?? "");
  const [afronding, setAfronding] = React.useState(huidigeRol?.werkdagen?.afronding ?? "");

  React.useEffect(() => {
    if (!huidigeRol) back();
  }, []);

  const v = Number(voorbereiding) || 0;
  const f = Number(festivaldagen) || 0;
  const a = Number(afronding) || 0;
  const totaalNum = v + f + a;

  React.useEffect(() => {
    setState(s => ({
      ...s,
      rollen: (s.rollen || []).map((r, i) => i === 0 ? {
        ...r,
        werkdagen: {
          voorbereiding: v,
          festivaldagen: f,
          afronding: a,
          totaal: totaalNum,
        },
      } : r),
    }));
  }, [v, f, a, totaalNum]);

  if (!huidigeRol) return null;

  const urenTotaal = totaalNum * UREN_PER_WERKDAG;
  const valid = totaalNum > 0;

  const clamp = (raw) => {
    if (raw === "") return "";
    return Math.max(0, Math.min(500, Number(raw) || 0));
  };

  const fmtUren = (u) => {
    if (Number.isInteger(u)) return `${u} uur`;
    return `${u.toFixed(1).replace(".", ",")} uur`;
  };

  return (
    <div>
      <StepHeader
        tag="Filmfestival"
        vorm={state.vorm}
        title="Hoeveel werkdagen besteed je aan deze opdracht?"
        sub="Bij filmfestivals rekenen we met een werkweek van 38 uur, zonder pauze. Dat betekent een werkdag van 7,6 uur. Dit staat in de Standaard Basisregeling Secundaire Arbeidsvoorwaarden Filmfestivals (SAV)."
        onBack={back}
      />

      <div className="role-summary" style={{
        display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap",
        padding: "14px 18px", marginBottom: 20,
        background: FW.cream, border: `1.5px solid ${FW.gray100}`, borderRadius: 14,
      }}>
        <div style={{ fontSize: 14, color: FW.gray500, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Je rol</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: FW.navy }}>{huidigeRol.rol}</div>
        <div style={{ fontSize: 14, color: FW.navy2 }}>· {huidigeRol.dep}</div>
        <NiveauBadge niveau={huidigeRol.niveau} />
      </div>

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{
            padding: 20, background: FW.white,
            border: `1.5px solid ${FW.gray100}`, borderRadius: 18,
          }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12, paddingBottom: 14, marginBottom: 16,
              borderBottom: `1px solid ${FW.gray100}`,
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: FW.navy, letterSpacing: "-0.005em" }}>Werkdagen</div>
              <div style={{
                fontSize: 13, fontWeight: 700,
                padding: "4px 12px", borderRadius: 999,
                background: totaalNum > 0 ? FW.beige2 : FW.gray50,
                color: totaalNum > 0 ? FW.navy : FW.gray500,
                flexShrink: 0,
              }}>{totaalNum} {totaalNum === 1 ? "dag" : "dagen"}</div>
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <DagenRij label="Voorbereiding festival" value={voorbereiding} onChange={(v) => setVoorbereiding(clamp(v))} />
              <DagenRij label="Festivaldagen"          value={festivaldagen} onChange={(v) => setFestivaldagen(clamp(v))} />
              <DagenRij label="Afronding festival"     value={afronding}     onChange={(v) => setAfronding(clamp(v))} />
            </div>
          </div>
        </div>

        <AsideCard>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <i className="ri-calculator-line" /> Lopend totaal
          </div>
          <div style={{ display: "grid", gap: 6, fontSize: 14, color: FW.navy2 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Werkdagen</span>
              <span style={{ fontWeight: 700, color: FW.navy }}>{totaalNum} {totaalNum === 1 ? "dag" : "dagen"}</span>
            </div>
          </div>
          <div style={{ height: 1, background: FW.gray100, margin: "12px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}>
            <span style={{ color: FW.navy2 }}>Omgerekend</span>
            <span style={{ fontWeight: 700, color: FW.navy }}>{fmtUren(urenTotaal)}</span>
          </div>
          <div style={{ fontSize: 12, color: FW.gray500, marginTop: 8, lineHeight: 1.4 }}>
            {totaalNum > 0
              ? `${totaalNum} werkdag${totaalNum === 1 ? "" : "en"} × 7,6 uur (SAV-norm, excl. pauze).`
              : "Eén werkdag wordt gerekend als 7,6 uur (excl. pauze, conform SAV)."}
          </div>
        </AsideCard>
      </div>

      <div className="step-nav" style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
        <FwButton variant="ghost" arrow={false} onClick={back}>← Terug</FwButton>
        <FwButton variant="primary" onClick={next} disabled={!valid}>
          Bekijk je Openingsbod
        </FwButton>
      </div>
    </div>
  );
};

const DagenRij = ({ label, value, onChange }) => {
  const num = Number(value) || 0;
  const actief = num > 0;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 10px",
      background: actief ? FW.cream : "transparent",
      borderRadius: 10,
      border: actief ? `1px solid ${FW.gray100}` : "1px solid transparent",
    }}>
      <div style={{ flex: 1, fontSize: 14, color: FW.navy, lineHeight: 1.4 }}>{label}</div>
      <DagenStepper value={num} onChange={onChange} />
      <span style={{ fontSize: 13, color: FW.gray500 }}>dagen</span>
    </div>
  );
};

const DagenStepper = ({ value, onChange }) => {
  const num = Number(value) || 0;
  const aanpassen = (delta) => onChange(Math.max(0, num + delta));
  return (
    <div style={{
      display: "inline-flex", alignItems: "stretch",
      border: `1.5px solid ${FW.gray100}`, borderRadius: 8,
      background: FW.white, overflow: "hidden",
    }}>
      <StepperBtn icon="ri-subtract-line" aria="Verlaag dagen"
        onClick={() => aanpassen(-1)} disabled={num === 0} />
      <input
        type="number" min="0"
        value={num || ""} placeholder="0"
        onChange={e => onChange(e.target.value)}
        style={{
          width: 36, border: 0, background: "transparent",
          padding: "5px 0", fontSize: 14, fontWeight: 700, color: FW.navy,
          fontFamily: "var(--font-sans)", textAlign: "center", outline: "none",
        }}
      />
      <StepperBtn icon="ri-add-line" aria="Verhoog dagen"
        onClick={() => aanpassen(1)} />
    </div>
  );
};

const StepperBtn = ({ icon, aria, onClick, disabled }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    aria-label={aria}
    disabled={disabled}
    onMouseEnter={e => !disabled && (e.currentTarget.style.background = FW.cream)}
    onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
    style={{
      width: 22, padding: 0,
      background: "transparent", border: 0,
      color: disabled ? FW.gray500 : FW.navy,
      cursor: disabled ? "default" : "pointer",
      display: "grid", placeItems: "center",
      opacity: disabled ? 0.35 : 1,
      transition: "background .15s",
    }}
  >
    <i className={icon} style={{ fontSize: 13 }} />
  </button>
);
